/**
 * Represents a node in the RBAC hierarchy.
 */
export interface RBACNode<TRole extends string, TPermission extends string> {
  /**
   * Child nodes that inherit permissions from this node.
   */
  children: RBACNode<TRole, TPermission>[];
  /**
   * Permissions associated with this node.
   */
  permissions: TPermission[];
  /**
   * Role associated with this node.
   */
  role: TRole;
}

/**
 * Implements a simple, hierarchical Role-Based Access Control system.
 */
export class RBAC<TRole extends string, TPermission extends string> {
  /**
   * Creates an RBAC instance with a given root node.
   *
   * @param root - The root node of the RBAC hierarchy.
   */
  constructor(private readonly root: RBACNode<TRole, TPermission>) {}

  /**
   * Check if a role has a specific permission, taking into account the inherited permissions from
   * its parent nodes.
   *
   * This method uses a depth-first search to traverse the tree structure, finding the path from
   * the root node to the node with the given role. If any node on that path has the required
   * permission, this method returns true.
   *
   * @param role - The role to check for permission.
   * @param permission - The permission to check.
   * @returns `true` if the role has the permission, `false` otherwise.
   */
  hasPermission(role: TRole, permission: TPermission): boolean {
    // Array to store the path from root to the role node
    const path: RBACNode<TRole, TPermission>[] = [];

    // Find the path and populate the 'path' array
    this.dfsFindPath(this.root, role, path);

    // Check if any node in the path has the required permission
    return path.some((node) => node.permissions.includes(permission));
  }

  /**
   * Recursive function to find the path from the root node to a node with the given role.
   *
   * @param node - The current node being processed.
   * @param targetRole - The role to find the path for.
   * @param currentPath - An array representing the current path being traversed.
   * @returns `true` if the role is found, `false` otherwise.
   */
  private dfsFindPath(
    node: RBACNode<TRole, TPermission>,
    targetRole: TRole,
    currentPath: RBACNode<TRole, TPermission>[],
  ): boolean {
    // Push the current node to the path
    currentPath.push(node);

    // Check if the current node has the target role
    if (node.role === targetRole) {
      return true;
    }

    // Recursively search in children nodes
    for (const child of node.children) {
      if (this.dfsFindPath(child, targetRole, currentPath)) {
        return true;
      }
    }

    // Remove the last node from the path since it didn't lead to the target role
    currentPath.pop();

    return false;
  }
}
